// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useState } from 'react';

import {
  leafStatusText,
  SECTION_TITLE,
  type GroupId,
  type GroupModel,
  type LeafId,
  type LeafModel,
  type SectionId,
} from '../../../bygning/rapportTree';
import { Pill } from '../../Pill';
import styles from './RapportTree.module.css';

export interface RapportTreeProps {
  groups: readonly GroupModel[];
  onSelect: (id: LeafId) => void;
}

const SECTIONS: readonly SectionId[] = ['rapporter', 'indberetning'];

/**
 * Rapporter & indberetning's left tile (`bygning/rapportTree.ts`): under
 * "Rapporter" each report folds open to its pages, its summary "Klar" or
 * "2 af 3 klar"; under "Indberetning" the submissions and exports. Every
 * leaf is a button with its Klar / Ufuldstændig (or "Snart"), the one in the
 * centre pressed. A report holding the shown leaf opens again when the leaf
 * changes (J/K into a folded report). On a phone the tile is a bar: the
 * leaves in one sideways-scrolling row, each report's name before its own.
 */
export function RapportTree({ groups, onSelect }: RapportTreeProps) {
  const [closed, setClosed] = useState<ReadonlySet<GroupId>>(() => new Set());
  const activeGroup = groups.find((g) => g.leaves.some((l) => l.active)) ?? null;
  const activeLeaf = activeGroup?.leaves.find((l) => l.active)?.def.id ?? null;
  const activeGroupId = activeGroup?.def.id ?? null;
  useEffect(() => {
    if (activeGroupId === null) return;
    setClosed((prev) => {
      if (!prev.has(activeGroupId)) return prev;
      const next = new Set(prev);
      next.delete(activeGroupId);
      return next;
    });
  }, [activeLeaf, activeGroupId]);

  const toggle = (id: GroupId, open: boolean) =>
    setClosed((prev) => {
      if (prev.has(id) === !open) return prev;
      const next = new Set(prev);
      if (open) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <nav className={styles.tree} aria-label="Rapporter og indberetning">
      <div className={styles.desktop}>
        {SECTIONS.map((section) => {
          const own = groups.filter((g) => g.def.section === section);
          if (own.length === 0) return null;
          const headingId = `rapport-tree-${section}`;
          return (
            <section key={section} className={styles.section} aria-labelledby={headingId}>
              <h2 id={headingId} className={styles.caption}>
                {SECTION_TITLE[section]}
              </h2>
              {own.map((g) =>
                g.def.collapsible ? (
                  <details
                    key={g.def.id}
                    className={styles.group}
                    open={!closed.has(g.def.id)}
                    onToggle={(e) => toggle(g.def.id, e.currentTarget.open)}
                  >
                    <summary className={styles.summary}>
                      <span className={styles.groupTitle}>{g.def.title}</span>
                      {g.rollup && <Pill tone={g.rollup.tone}>{g.rollup.text}</Pill>}
                    </summary>
                    <Leaves leaves={g.leaves} onSelect={onSelect} />
                  </details>
                ) : (
                  <Leaves key={g.def.id} leaves={g.leaves} onSelect={onSelect} />
                ),
              )}
            </section>
          );
        })}
      </div>

      <div className={styles.phone}>
        {groups.map((g) => (
          <div key={g.def.id} className={styles.phoneGroup} role="group" aria-label={g.def.title}>
            <span className={styles.phoneLabel} aria-hidden="true">
              {g.def.title}
            </span>
            {g.leaves.map((l) => (
              <LeafButton key={l.def.id} leaf={l} onSelect={onSelect} />
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
}

function Leaves({ leaves, onSelect }: { leaves: readonly LeafModel[]; onSelect: (id: LeafId) => void }) {
  return (
    <ul className={styles.leaves}>
      {leaves.map((l) => (
        <li key={l.def.id}>
          <LeafButton leaf={l} onSelect={onSelect} />
        </li>
      ))}
    </ul>
  );
}

function LeafButton({ leaf, onSelect }: { leaf: LeafModel; onSelect: (id: LeafId) => void }) {
  const status = leafStatusText(leaf.status);
  return (
    <button
      type="button"
      className={`${styles.leaf} ${leaf.active ? styles.active : ''}`}
      aria-pressed={leaf.active}
      title={leaf.reasons.length > 0 ? leaf.reasons.join('\n') : undefined}
      onClick={() => onSelect(leaf.def.id)}
    >
      <span className={styles.leafTitle}>{leaf.def.title}</span>
      {status &&
        (leaf.status === 'soon' ? (
          <Pill variant="outline">{status}</Pill>
        ) : (
          <Pill tone={leaf.status === 'ready' ? 'good' : 'warn'}>{status}</Pill>
        ))}
    </button>
  );
}
